import Box from '@mui/material/Box';
import { concat, equals, length, pathOr } from 'ramda';
import React from 'react';
import { useHistory } from 'react-router';

import BellIcon from 'src/assets/icons/bell.svg';
import UnreadBellIcon from 'src/assets/icons/unread-bell.svg';
import NotificationDropdown from 'src/features/TopMenu/Notification/NotificationDrop';
import {
  applicationUseMutation,
  useSubscriptionApplication,
} from 'src/graphqlhooks';
import useAccounts from 'src/hooks/useAccountsHook';
import { ZERO } from 'src/services/constants';
import {
  BULK_UPDATE_NOTIFICATIONS,
  NOTIFICATION_BY_ACCOUNT_ID_QUERY,
  READ_NOTIFICATIONS_QUERY,
} from 'src/services/grapqlQueries/notificationsQuery';
import { getNamespaceName } from 'src/services/objects/namespaces';
import SiteSettings from 'src/siteSetting';
import { isNotEmpty } from 'src/utilities/hasValue';
import { mkUpdateNotificationAsSeen } from 'src/utilities/notifications/pushNotifications';
import { authenticatedUser } from 'src/utilities/storage';

import InAppNotification from './InAppNotification';

interface Props {
  accounts: any;
  advisorPersona: any;
}

const NotificationLanding: React.FC<Props> = (props: Props) => {
  const { accounts, advisorPersona } = props;
  const { shouldEnableInAppNotification } = useAccounts();
  const [notifications, setNotifications]: any = React.useState([]);
  const [readNotifications, setReadNotifications] = React.useState([]);
  const [newUnreadMsgCount, setNewUnreadMsgCount] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(false);
  const role = advisorPersona || pathOr([], ['roles_id'], accounts);
  const [updateNotifications] = applicationUseMutation(
    BULK_UPDATE_NOTIFICATIONS,
    {},
  );
  const history = useHistory();

  useSubscriptionApplication(
    {
      query: NOTIFICATION_BY_ACCOUNT_ID_QUERY,
      variables: {
        accountId: pathOr('', ['id'], accounts),
        namespace: {
          namespace: getNamespaceName(),
        },
        role,
      },
    },
    ({ data, errors }: any) => {
      if (errors && length(errors) > 0) {
        return;
      }
      const _notifications = pathOr([], ['notifications'], data);

      setNotifications(_notifications);

      const shouldTriggerInAppNotification =
        isNewNotification(_notifications) &&
        shouldEnableInAppNotification &&
        isNotEmpty(_notifications) &&
        !isCurrentlyViewingChat(_notifications);

      if (shouldTriggerInAppNotification) {
        setIsOpen(true);
      }
      setNewUnreadMsgCount(length(_notifications));
    },
  );

  useSubscriptionApplication(
    {
      query: READ_NOTIFICATIONS_QUERY,
      variables: {
        accountId: pathOr('', ['id'], accounts),
        namespace: {
          namespace: getNamespaceName(),
        },
      },
    },
    ({ data, errors }: any) => {
      if (errors && length(errors) > 0) {
        return;
      }
      const _notifications: any = pathOr([], ['notifications'], data);
      setReadNotifications(_notifications);
    },
  );

  const isNewNotification = (notifications: any) => {
    if (equals(length(notifications), 0)) {
      return false;
    }

    const latestNotifcationId = pathOr('', ['0', 'id'], notifications);
    const cachedlatestNotificationId =
      authenticatedUser.recentNotificationId.get();

    if (latestNotifcationId === cachedlatestNotificationId) {
      return false;
    }
    authenticatedUser.recentNotificationId.set(latestNotifcationId);
    return true;
  };

  const updateRecentNotificationIdWhenMarkedRead = (index: number) => {
    if (index > 0) {
      return;
    }

    const latestNotifcationId = pathOr('', ['1', 'id'], notifications);
    authenticatedUser.recentNotificationId.set(latestNotifcationId);
  };

  const isCurrentlyViewingChat = (notifications: any) => {
    if (equals(length(notifications), 0)) {
      return false;
    }

    const redirectTo = pathOr('', ['0', 'data', 'redirect_to'], notifications);
    if (!equals(redirectTo, window.location.href)) {
      return false;
    }

    const url = new URL(redirectTo);
    return url.searchParams.get('thisurl') === 'Chat';
  };

  const handleRedirect = (redirectUrl: string) => {
    if (isNotEmpty(redirectUrl)) {
      const url = new URL(redirectUrl);
      history.push(concat(url.pathname, url.search));
    }
    setIsOpen(false);
  };

  const handleReadMessage = (notification: any, index: number, isMarkAsRead: boolean = false) => {
    updateRecentNotificationIdWhenMarkedRead(index);
    updateNotifications({
      variables: {
        notifications: mkUpdateNotificationAsSeen([notification]),
      },
    });
    !isMarkAsRead && handleRedirect(pathOr('', ['data', 'redirect_to'], notification));
  };


  const handleMarkAsCompleteAll = () => {
    authenticatedUser.recentNotificationId.set('');
    updateNotifications({
      variables: {
        notifications: mkUpdateNotificationAsSeen(notifications),
      },
    });
  };



  const triggerNotificationDropdown = (e: any) => {
    if (!equals(newUnreadMsgCount, Number(ZERO))) {
      updateNotifications({
        variables: {
          notifications: mkUpdateNotificationAsSeen(notifications),
        },
      });
    }
    setIsOpen(e.currentTarget);
  };

  const triggerInApp = () => {
    setIsOpen(true);
  };

  const handleClick = (e: any) => {
    if (!shouldEnableInAppNotification) {
      triggerNotificationDropdown(e);
      return;
    }

    triggerInApp();
  };

  return (
    <Box>
      <Box mr={-1} data-test-id={'t--bell-icon'} onClick={handleClick} pr={1.5} sx={{ cursor: 'pointer' }}>
        {newUnreadMsgCount > 0 ? (
          <UnreadBellIcon data-test-id={'t--unread-bell-icon'} color={SiteSettings.mainColor()} />
        ) : (
          <BellIcon data-test-id={'t--read-bell-icon'}  color={SiteSettings.mainColor()} />
        )}
      </Box>
      {shouldEnableInAppNotification ? (
        <InAppNotification
          handleReadMessage={handleReadMessage}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          unReadMsgCount={newUnreadMsgCount}
          onClearAll={handleMarkAsCompleteAll}
          unReadNotifications={notifications}
        />
      ) : (
        <NotificationDropdown
          isDropDown={isOpen}
          readNotifications={readNotifications}
          setIsDropDown={setIsOpen}
          unReadMsgCount={newUnreadMsgCount}
          unReadNotifications={notifications}
        />
      )}
    </Box>
  );
};

export default NotificationLanding;
